import { callWithAuth, postAppleVerifyTransaction } from "@/api/Api.ts";

export const getPlatform = (): string => {
  if (
    window.webkit?.messageHandlers["iap-purchase-request"] &&
    window.webkit?.messageHandlers["iap-transactions-request"] &&
    window.webkit?.messageHandlers["iap-products-request"]
  ) {
    return "apple";
  }
  // Add logic for detecting Android and Web platforms
  return "web"; // Default to web if no specific platform is detected
};

export interface Product {
  sku: string;
  price: string; // returns price in weekly units
  name: string; // something like monthly or weekly
  popular: boolean; // marks the most attractive offer
  currency: string; // add currency property
  offer?: { currencyCode: string; price: number }[];
}

// Function to get products for the current platform
export const getProducts = async (): Promise<Product[]> => {
  const weeklySubId = "weekly_sub";
  const monthlySubId = "monthly_sub";
  let products: Product[] = [];
  const platform = getPlatform();
  if (platform === "apple") {
    // Simulate fetching products for Apple
    const appleProducts = await getProductsApple();

    products = appleProducts.map((product) => {
      return {
        sku: product.attributes.offerName,
        price: price(product.attributes.offers).toFixed(2),
        currency: product.attributes.offers[0].currencyCode,
        name: makeProductName(product.attributes.offerName),
        popular: product.attributes.offerName === monthlySubId,
        offer: product.attributes.offers.map((offer) => ({
          currencyCode: offer.currencyCode,
          price: parseFloat(offer.price),
        })),
      };
    });
  } else if (platform === "web") {
    products = getProductsStripe();
  }

  // important make sure this matches the sku for all the products
  products = products.sort((a) => {
    if (a.sku === weeklySubId) {
      return -1;
    }
    return 1;
  });
  // Add logic for fetching products on other platforms
  return products;
};

function price(
  offers: { currencyCode: string; price: string; priceFormatted: string }[]
) {
  if (offers.length === 0) {
    return 0;
  }
  return parseFloat(offers[0].price);
}

function getProductsStripe(): Product[] {
  const products = [
    {
      price: "9.99",
      sku: "monthly_sub",
      name: "Monthly",
      popular: true,
      currency: "USD",
    },
    {
      price: "5.99",
      sku: "weekly_sub",
      name: "Weekly",
      popular: false,
      currency: "USD",
    },
  ];
  return products;
}

function makeProductName(sku: string) {
  if (sku === "weekly_sub") {
    return "Weekly";
  }
  return "Monthly";
}

type AppleProduct = {
  attributes: {
    description: {
      standard: string;
    };
    icuLocale: string;
    isFamilyShareable: number;
    kind: string;
    name: string;
    offerName: string;
    offers: {
      currencyCode: string;
      price: string;
      priceFormatted: string;
    }[];
  };
  href: string;
  id: string;
  type: string;
};

type AppleTransaction = {
  originalPurchaseDate: number;
  deviceVerificationNonce: string;
  subscriptionGroupIdentifier: string;
  environment: string;
  inAppOwnershipType: string;
  originalTransactionId: string;
  transactionReason: string;
  storefront: string;
  signedDate: number;
  bundleId: string;
  storefrontId: string;
  transactionId: string;
  type: string;
  webOrderLineItemId: string;
  isUpgraded: boolean;
  productId: string;
  deviceVerification: string;
  purchaseDate: number;
  expiresDate: number;
  quantity: number;
  OfferDiscountType: string;
};

async function getProductsApple(): Promise<AppleProduct[]> {
  return new Promise((resolve) => {
    window.addEventListener("iap-products-result", (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail) {
        resolve(JSON.parse(customEvent.detail));
      }
    });
    window.webkit?.messageHandlers["iap-products-request"].postMessage([
      "monthly_sub",
      "weekly_sub",
    ]);
  });
}

// Function to make a purchase
// this function also verifies the transaction ID. We throw an error
// we return true if the transaction was a success
// and false when it was wrong.
export async function makePurchase(
  sku: string,
  signal: AbortSignal
): Promise<boolean | null> {
  const platform = getPlatform();
  if (platform === "apple") {
    try {
      const transaction = await appleMakePurchase(sku, signal);
      const good = await appleVerifyTransaction(transaction);
      return good;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        console.log("Purchase was aborted");
        return null; // Indicate that the purchase was aborted
      }
      return false;
    }
  }
  console.error("Platform not supported");
  return false;
}

// this function makes a purchase and returns the transaction ID
const appleMakePurchase = (
  sku: string,
  signal: AbortSignal
): Promise<AppleTransaction> => {
  return new Promise((resolve, reject) => {
    // Check if the request is aborted
    if (signal.aborted) {
      return reject(new DOMException("Aborted", "AbortError"));
    }

    // Use the purchaseRequest function from AppleIAPCard.tsx
    const transactionsRequest = () => {
      if (window.webkit?.messageHandlers["iap-transactions-request"]) {
        window.webkit.messageHandlers["iap-transactions-request"].postMessage(
          "request"
        );
      }
    };

    const handleTransactionsResult = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail) {
        let transaction: AppleTransaction[] = JSON.parse(customEvent.detail);
        console.log("transaction", transaction);
        if (transaction.length > 0) {
          transaction = transaction.sort(
            (a, b) => b.purchaseDate - a.purchaseDate
          );
          resolve(transaction[0]);
        } else {
          reject(new Error("Transaction failed"));
        }
      }
    };
    const handlePurchaseResult = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent?.detail === "success") {
        transactionsRequest();
      } else {
        window.dispatchEvent(new CustomEvent("payment-window-closed"));
      }
      window.removeEventListener("iap-purchase-result", handlePurchaseResult);
    };

    window.addEventListener("iap-purchase-result", handlePurchaseResult);
    window.addEventListener(
      "iap-transactions-result",
      handleTransactionsResult
    );

    window.webkit.messageHandlers["iap-purchase-request"].postMessage(
      JSON.stringify({ productID: sku, quantity: 1 })
    );

    // Listen for abort event
    signal.addEventListener("abort", async () => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      window.removeEventListener("iap-purchase-result", handlePurchaseResult);
      window.removeEventListener(
        "iap-transactions-result",
        handleTransactionsResult
      );
      reject(new DOMException("Aborted", "AbortError"));
    });
  });
};

// this function verifies the transaction ID
// true if the transaction is good and the user will receive their premium status
// false if something went wrong
async function appleVerifyTransaction(
  transaction: AppleTransaction
): Promise<boolean> {
  try {
    const response = await callWithAuth(postAppleVerifyTransaction, {
      transactionId: transaction.transactionId,
    });

    if (response.message === "success") {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
}
